import { ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faMinus } from '@fortawesome/pro-solid-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  SitterService,
  TeachingSkillsService,
  SpecialExperiencesService,
  SpecialServicesService,
  SpecialSituationsService,
  SitterSpecialSituationsService,
  LanguageService,
  SitterTeachingSkillsService,
  YearExperienceService,
  SitterCollegeService,
  CertificationsService,
  SitterStatsService,
  FavoriteSitterService,
  SitterRateSuggestionService,
  SitterProfileStatusService,
  SitterProfileStatus,
} from 'us-web-services';
import { SitterNoteModal } from 'us-react-components/dist/SitterNoteModal/SitterNoteModal';
import { FlagProfileModal } from 'us-react-components/dist/FlagProfileModal/FlagProfileModal';
import { ConfirmModal } from 'us-react-components/dist/ConfirmModal/ConfirmModal';
import { ProfileVideoModal } from 'us-react-components/dist/ProfileVideoModal/ProfileVideoModal';
import { toggleSitterBlock } from 'us-react-components/dist/utils/sitterBlock';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { checkForPaywall } from '../../utils/PaywallUtil';
import { SitterProfileChrome } from '../SitterReviews/components/SitterProfileChrome';
import {
  NotAcceptingFamiliesBanner,
  SitterSnippet,
  SitterAbout,
  SitterOtherServices,
  SitterBadges,
  SitterAgeExperience,
  SitterRates,
  SitterTeachingSkills,
  SitterSpecialExperience,
  SitterSpecialServices,
  SitterSpecialSituations,
  SitterEducation,
  SitterCertifications,
  SitterLocation,
  AvailabilityStatusWrapper,
  SitterRelationship,
  SitterProfileSidePanel,
  SitterExtras,
} from './components/SubComponents';
import {
  AvailabilityModal,
  BasicsAndBioEditModal,
  MultiSelectEditModal,
  OtherServicesEditModal,
  RatesEditModal,
  LocationEditModal,
  AgeExperienceEditModal,
  EducationEditModal,
  CertificationsEditModal,
} from './components/modals';

// Collapsible section wrapper. Mirrors Angular's `.gray-bkgd-box` + `.box-header`
// + `.box-content-toggle` (+/- icons) pattern used on Experience/Background/
// Location wrappers — click the header to hide/show the body.
const CollapsibleSection = ({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}): JSX.Element => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className='bg-neutral-50 border border-neutral-200 rounded-lg p-6'>
      <button
        type='button'
        aria-expanded={open}
        onClick={(): void => setOpen((v) => !v)}
        className='w-full flex items-center justify-between mb-4 pb-2 border-b border-neutral-200 text-left'
      >
        <h3 className='text-lg font-semibold m-0'>{title}</h3>
        <FontAwesomeIcon
          icon={open ? faMinus : faPlus}
          className='text-neutral-500'
          aria-hidden='true'
        />
      </button>
      {open && children}
    </div>
  );
};

const formatRate = (rate?: { minimumRate?: number; maximumRate?: number }): string => {
  if (!rate || (!rate.minimumRate && !rate.maximumRate)) return '';
  if (rate.minimumRate) {
    const min = `$${rate.minimumRate}`;
    const max =
      rate.maximumRate && rate.maximumRate !== rate.minimumRate
        ? ` - ${rate.maximumRate}`
        : '';
    return `${min}${max} per hour`;
  }
  return `$${rate.maximumRate} per hour`;
};

export const SitterProfile = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const [sitter, setSitter] = useState<SitterExtras | null>(null);
  const [profileStatus, setProfileStatus] = useState<SitterProfileStatus | null>(null);
  const [stats, setStats] = useState<Parameters<typeof AvailabilityStatusWrapper>[0]['stats']>(undefined);
  const [education, setEducation] = useState<{
    id?: number;
    college?: { id?: number; name?: string };
    areaOfStudy?: { id?: number; name?: string };
    graduation?: string;
  } | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [hideConfirmOpen, setHideConfirmOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [editAboutOpen, setEditAboutOpen] = useState(false);
  const [editTeachingSkillsOpen, setEditTeachingSkillsOpen] = useState(false);
  const [editSpecialExpOpen, setEditSpecialExpOpen] = useState(false);
  const [editSpecialServicesOpen, setEditSpecialServicesOpen] = useState(false);
  const [editSpecialSituationsOpen, setEditSpecialSituationsOpen] = useState(false);
  const [editLanguagesOpen, setEditLanguagesOpen] = useState(false);
  const [editOtherServicesOpen, setEditOtherServicesOpen] = useState(false);
  const [editRatesOpen, setEditRatesOpen] = useState(false);
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [editAgeOpen, setEditAgeOpen] = useState(false);
  const [editEducationOpen, setEditEducationOpen] = useState(false);
  const [editCertificationsOpen, setEditCertificationsOpen] = useState(false);
  const [teachingSkillsOptions, setTeachingSkillsOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [specialExpOptions, setSpecialExpOptions] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [specialServicesOptions, setSpecialServicesOptions] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [specialSituationsOptions, setSpecialSituationsOptions] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [languagesOptions, setLanguagesOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [ageExperiencesOptions, setAgeExperiencesOptions] = useState<Array<{ id: number; label: string }>>([]);
  const [certificationsOptions, setCertificationsOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [suggestedRate, setSuggestedRate] = useState<
    { rate?: number; defaultRate?: boolean } | undefined
  >(undefined);

  useEffect(() => {
    document.title = 'UrbanSitter - Caregiver Profile';
  }, []);

  useEffect(() => {
    const sitterId = Number(pathId);
    const userId = user?.id;
    if (!sitterId || !userId) return;
    const isParentViewer = !!user?.parent;
    void (async (): Promise<void> => {
      try {
        // Angular sends `parentId` only when the viewer is acting as a parent
        // (sitter-object-service.js:82-84). Including parentId for a caregiver
        // viewing their own profile makes the API strip private address fields
        // (street/street2/zip detail) — which is why "Apt #" appeared empty on
        // re-open after a successful save.
        const params: { userId: number; parentId?: number } = { userId };
        if (isParentViewer) params.parentId = userId;
        const resp = await SitterService.get(sitterId, { params });
        setSitter((resp.data?.data ?? null) as SitterExtras | null);
      } catch (err) {
        console.warn('[SitterProfile] load failed', err);
      } finally {
        setLoaded(true);
      }
    })();
    if (sitterId === user.id) {
      void (async (): Promise<void> => {
        try {
          const resp = await SitterProfileStatusService.get(sitterId);
          setProfileStatus((resp.data?.data ?? null) as SitterProfileStatus | null);
        } catch {
          setProfileStatus(null);
        }
      })();
    }
  }, [pathId, user?.id]);

  useEffect(() => {
    void (async (): Promise<void> => {
      try {
        const resp = await TeachingSkillsService.getByFilter();
        setTeachingSkillsOptions((resp.data?.data ?? []) as Array<{ id: number; name: string }>);
      } catch {
        setTeachingSkillsOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await SpecialExperiencesService.getByFilter();
        setSpecialExpOptions((resp.data?.data ?? []) as Array<{ id: number; name: string; description?: string }>);
      } catch {
        setSpecialExpOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await SpecialServicesService.getByFilter();
        setSpecialServicesOptions((resp.data?.data ?? []) as Array<{ id: number; name: string; description?: string }>);
      } catch {
        setSpecialServicesOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await SpecialSituationsService.getByFilter();
        setSpecialSituationsOptions((resp.data?.data ?? []) as Array<{ id: number; name: string; description?: string }>);
      } catch {
        setSpecialSituationsOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await LanguageService.getByFilter();
        setLanguagesOptions((resp.data?.data ?? []) as Array<{ id: number; name: string }>);
      } catch {
        setLanguagesOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await YearExperienceService.getByFilter();
        setAgeExperiencesOptions(
          (resp.data?.data ?? []).map((e) => ({ id: e.id, label: e.label })),
        );
      } catch {
        setAgeExperiencesOptions([]);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await CertificationsService.getByFilter();
        setCertificationsOptions(
          (resp.data?.data ?? []).map((c) => ({ id: c.id, name: c.name })),
        );
      } catch {
        setCertificationsOptions([]);
      }
    })();
  }, []);

  useEffect(() => {
    const sitterId = Number(pathId);
    if (!sitterId) return;
    void (async (): Promise<void> => {
      try {
        const resp = await SitterStatsService.get(sitterId);
        setStats(resp.data?.data);
      } catch {
        setStats(undefined);
      }
    })();
    void (async (): Promise<void> => {
      try {
        const resp = await SitterCollegeService.getByFilter({ params: { sitterId } });
        const list = (resp.data?.data ?? []) as Array<{
          id?: number;
          college?: { id?: number; name?: string };
          areaOfStudy?: { id?: number; name?: string };
          graduation?: string;
        }>;
        setEducation(list[0]);
      } catch {
        setEducation(undefined);
      }
    })();
  }, [pathId]);

  useEffect(() => {
    const sitterId = sitter?.id;
    const zip = sitter?.address?.zip;

    if (!sitterId || !zip) return;

    const load = async (): Promise<void> => {
      try {
        const resp = await SitterRateSuggestionService.get({
          params: { kids: 1, sitterId, zip },
        });
        setSuggestedRate(resp.data?.data as typeof suggestedRate);
      } catch {
        setSuggestedRate(undefined);
      }
    };
    void load();

  }, [sitter?.id, sitter?.address?.zip]);

  const refreshSitter = async (): Promise<void> => {
    const sitterId = Number(pathId);
    const userId = user?.id;
    if (!sitterId || !userId) return;
    const params: { userId: number; parentId?: number } = { userId };
    if (user?.parent) params.parentId = userId;
    const resp = await SitterService.get(sitterId, { params });
    setSitter((resp.data?.data ?? null) as SitterExtras | null);
  };

  if (!loaded) {
    return (
      <div className='container py-8 text-center'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (sitter && sitter.active === false) {
    return (
      <div className='container py-8'>
        <h2 className='text-lg'>{t('pages.sitterProfile.notActive')}</h2>
      </div>
    );
  }

  if (!sitter?.id) return <div className='container py-8' />;

  const isSelf = !!user?.id && user.id === sitter.id;
  const isParent = !!user?.parent;
  const isSitter = !!user?.sitter;
  // Caregiver-app viewers are other sitters, not parent members, so the
  // favorite heart (which is gated on `isMember` in Angular's sitter-snippet)
  // should not render here.
  const isMember = false;
  const canEdit = isSelf;
  const recommendLink =
    typeof window !== 'undefined'
      ? `${window.location.origin}/app/sitter/${sitter.id}/recommend`
      : `/app/sitter/${sitter.id}/recommend`;

  const rate = (sitter.rates ?? []).find(
    (r) => (r.minimumRate ?? 0) > 0 || (r.maximumRate ?? 0) > 0,
  );
  const rateText = formatRate(rate);
  const rateLabel = rateText ? ' for 1 child' : '';

  const trackProfileAction = (action: string): void => {
    tracker.track('clicked-button', { action, component: 'Sitter Profile' });
  };
  // Identity verification is a parent-only flow; in the caregiver app
  // SitterProfile is shown to a sitter, so the unverified branch is a no-op.
  const openIdentity = (): void => undefined;
  const onBook = (): void => {
    trackProfileAction('Book CTA');
    stores.verification.checkVerification(() => {
      navigate(`/parent/bookings/select?sitterId=${sitter.id}`);
    }, openIdentity);
  };
  const onMessage = (): void => {
    trackProfileAction('Message CTA');
    stores.verification.checkVerification(() => {
      checkForPaywall({
        userId: user?.id,
        sitter,
        action: (): void => {
          navigate(`/parent/${user?.id}/messages/${sitter.id}`);
        },
        openPaywall: (): void => {
          if (user?.id) {
            navigate(`/parent/${user.id}/buy-membership?sitterId=${sitter.id}`);
          }
        },
      });
    }, openIdentity);
  };
  const onInterview = (): void => {
    trackProfileAction('Interview CTA');
    stores.verification.checkVerification(() => {
      navigate(`/parent/interviews/select?sitterId=${sitter.id}`);
    }, openIdentity);
  };

  const toggleHidden = (): Promise<void> => {
    if (!user?.id || sitter.id === undefined) return Promise.resolve();
    return toggleSitterBlock(sitter, user.id, refreshSitter);
  };

  const saveAbout = async (data: { about: string; dob: string; english: string }): Promise<void> => {
    await SitterService.patch(sitter.id as number, {
      ...sitter,
      about: data.about,
      dob: data.dob,
      english: data.english,
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success(t('pages.sitterProfile.toasts.bioSaved'));
    await refreshSitter();
  };

  // Teaching skills, like special situations, are stored as join records via
  // SitterTeachingSkillsService. Two bugs combined to 400 the React port:
  //   1. POST body used `teachingSkillId: id` — the API expects the Angular
  //      shape `teachingSkill: { id }` (nested object).
  //   2. DELETE was called with the *teaching skill* id; the endpoint takes
  //      the *link record* id. Same fix pattern as saveSpecialSituations:
  //      load the existing join records, diff against the new selection.
  const saveTeachingSkills = async (ids: number[]): Promise<void> => {
    if (!sitter.id) return;
    try {
      const resp = await SitterTeachingSkillsService.getByFilter({
        params: { sitterId: sitter.id, limit: 30 },
      });
      const existing = (resp.data?.data ?? []) as Array<{
        id?: number;
        teachingSkill?: { id?: number };
      }>;
      const existingByItemId = new Map<number, number>();
      existing.forEach((row) => {
        const itemId = row.teachingSkill?.id;
        if (itemId && row.id) existingByItemId.set(itemId, row.id);
      });
      const toAdd = ids.filter((id) => !existingByItemId.has(id));
      const toRemove = [...existingByItemId.entries()]
        .filter(([itemId]) => !ids.includes(itemId))
        .map(([, linkId]) => linkId);
      for (const id of toAdd) {
        await SitterTeachingSkillsService.create({
          sitterId: sitter.id,
          teachingSkill: { id },
        } as never);
      }
      for (const linkId of toRemove) {
        await SitterTeachingSkillsService.delete(linkId);
      }
      toast.success(t('pages.sitterProfile.toasts.teachingSkillsUpdated'));
      await refreshSitter();
    } catch {
      toast.error(t('pages.sitterProfile.toasts.updateError'));
    }
  };

  const savePatchField = async (
    field: 'specialExperiences' | 'specialServices' | 'languages',
    options: Array<{ id: number; name: string; type?: string }>,
    ids: number[],
    otherName?: string,
  ): Promise<void> => {
    if (!sitter.id) return;
    const newItems: Array<{ id?: number; name?: string; type?: string }> = options
      .filter((o) => ids.includes(o.id))
      .map((o) => ({ id: o.id, name: o.name, type: o.type }));
    // Languages-only: append any "Other:" free-form entry as a name-only item.
    if (field === 'languages' && otherName) {
      const trimmed = otherName.trim();
      if (trimmed && !options.some((o) => o.name.toLowerCase() === trimmed.toLowerCase())) {
        newItems.push({ name: trimmed });
      }
    }
    try {
      await SitterService.patch(sitter.id, {
        ...sitter,
        [field]: newItems,
      } as unknown as Parameters<typeof SitterService.patch>[1]);
      toast.success(t('common.saved'));
      await refreshSitter();
    } catch {
      toast.error(t('pages.sitterProfile.toasts.saveError'));
    }
  };

  // Special situations use individual create/delete link records via
  // SitterSpecialSituationsService (Angular pattern). PATCHing the sitter
  // object with a specialSituations array is silently ignored by the API,
  // which is why the React port said "saved" but nothing persisted.
  const saveSpecialSituations = async (ids: number[]): Promise<void> => {
    if (!sitter.id) return;
    try {
      const resp = await SitterSpecialSituationsService.getByFilter({
        params: { sitterId: sitter.id, limit: 30 },
      });
      const existing = (resp.data?.data ?? []) as Array<{
        id?: number;
        specialSituation?: { id?: number };
      }>;
      const existingByItemId = new Map<number, number>();
      existing.forEach((row) => {
        const itemId = row.specialSituation?.id;
        if (itemId && row.id) existingByItemId.set(itemId, row.id);
      });
      const toAdd = ids.filter((id) => !existingByItemId.has(id));
      const toRemove = [...existingByItemId.entries()]
        .filter(([itemId]) => !ids.includes(itemId))
        .map(([, linkId]) => linkId);
      for (const id of toAdd) {
        await SitterSpecialSituationsService.create({
          sitterId: sitter.id,
          specialSituation: { id },
        } as never);
      }
      for (const linkId of toRemove) {
        await SitterSpecialSituationsService.delete(linkId);
      }
      toast.success(t('common.saved'));
      await refreshSitter();
    } catch {
      toast.error(t('pages.sitterProfile.toasts.saveError'));
    }
  };

  // Preset language names (used to split Other: out of sitter.languages for the modal)
  const presetLanguageNames = new Set(languagesOptions.map((l) => l.name.toLowerCase()));
  const initialOtherLanguage = (sitter.languages ?? [])
    .filter((l) => !!l)
    .filter((l) => !l.id || (l.name && !presetLanguageNames.has(l.name.toLowerCase())))
    .map((l) => l.name ?? '')
    .filter(Boolean)
    .join(', ');

  const saveOtherServices = async (patch: {
    willSitDog: boolean;
    willSitCat: boolean;
    willSitOtherPet: boolean;
    willSitSenior: boolean;
    willSitHome: boolean;
    petSittingExperience?: string;
    seniorSittingExperience?: string;
    homeSittingExperience?: string;
    specialServices: Array<{ id: number; name: string }>;
  }): Promise<void> => {
    if (!sitter.id) return;
    try {
      await SitterService.patch(sitter.id, {
        ...sitter,
        ...patch,
      } as unknown as Parameters<typeof SitterService.patch>[1]);
      toast.success(t('pages.sitterProfile.toasts.otherServicesUpdated'));
      await refreshSitter();
    } catch {
      toast.error(t('pages.sitterProfile.toasts.saveError'));
    }
  };

  const saveRates = async (patch: {
    rates: Array<{ minimumRate: number; maximumRate: number }>;
    maxKids?: number;
    minimumHours?: number;
    acceptsFixedRates?: boolean;
    willSitSingleFamily?: boolean;
  }): Promise<void> => {
    if (!sitter.id) return;
    await SitterService.patch(sitter.id, {
      ...sitter,
      ...patch,
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success(t('pages.sitterProfile.toasts.ratesUpdated'));
    await refreshSitter();
  };

  const saveLocation = async (patch: {
    address: {
      street?: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      neighborhood?: string;
    };
    worksWithin?: number;
  }): Promise<void> => {
    if (!sitter.id) return;
    await SitterService.patch(sitter.id, {
      ...sitter,
      ...patch,
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success(t('pages.sitterProfile.toasts.locationUpdated'));
    await refreshSitter();
  };

  const saveAgeExperiences = async (experiences: {
    infant?: { id: number };
    toddler?: { id: number };
    school?: { id: number };
  }): Promise<void> => {
    if (!sitter.id) return;
    const lookup = (id?: number): { id: number; label?: string } | undefined => {
      if (id == null) return undefined;
      const found = ageExperiencesOptions.find((a) => a.id === id);
      return found ? { id: found.id, label: found.label } : undefined;
    };
    await SitterService.patch(sitter.id, {
      ...sitter,
      experiences: {
        infant: lookup(experiences.infant?.id),
        toddler: lookup(experiences.toddler?.id),
        school: lookup(experiences.school?.id),
      },
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success(t('pages.sitterProfile.toasts.experienceUpdated'));
    await refreshSitter();
  };

  const saveEducation = async (data: {
    id?: number;
    college: { id?: number; name: string };
    areaOfStudy?: { id?: number; name: string };
    graduationMonth?: string;
    graduationYear?: string;
  }): Promise<void> => {
    if (!sitter.id || !data.college?.id) return;
    const graduation = ((): string | null => {
      if (!data.graduationMonth || !data.graduationYear) return null;
      const d = new Date(`${data.graduationMonth} 1, ${data.graduationYear}`);
      const pad = (n: number): string => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
    })();
    const payload = {
      id: data.id,
      sitterId: sitter.id,
      college: { id: data.college.id },
      areaOfStudy: data.areaOfStudy?.id ? { id: data.areaOfStudy.id } : undefined,
      graduation,
    } as unknown as Parameters<typeof SitterCollegeService.patch>[1];
    const resp = data.id
      ? await SitterCollegeService.patch(data.id, payload)
      : await SitterCollegeService.create(payload as never);
    setEducation(resp.data?.data as typeof education);
    toast.success(t('pages.sitterProfile.toasts.educationUpdated'));
    await refreshSitter();
  };

  const deleteEducation = async (id: number): Promise<void> => {
    await SitterCollegeService.delete(id);
    setEducation(undefined);
    toast.success(t('pages.sitterProfile.toasts.educationCleared'));
    await refreshSitter();
  };

  const saveCertifications = async (
    selected: Array<{ id: number; name: string }>,
  ): Promise<void> => {
    if (!sitter.id) return;
    await SitterService.patch(sitter.id, {
      ...sitter,
      certifications: selected,
    } as unknown as Parameters<typeof SitterService.patch>[1]);
    toast.success(t('pages.sitterProfile.toasts.certificationsUpdated'));
    await refreshSitter();
  };

  const toggleFavorite = async (): Promise<void> => {
    if (!sitter.id) return;
    try {
      if (sitter.relationship?.bookmarked) {
        const favId = (sitter.relationship as { bookmarkedId?: number }).bookmarkedId;
        if (favId) await FavoriteSitterService.delete(favId);
      } else {
        await FavoriteSitterService.create({
          sitterId: sitter.id,
          parentId: user?.id,
        } as never);
      }
      await refreshSitter();
    } catch {
      toast.error(t('pages.sitterProfile.toasts.favoriteError'));
    }
  };

  const approvalBanner =
    isSelf && profileStatus?.hiddenReason?.id && profileStatus.hiddenReason.id !== 0
      ? profileStatus
      : null;

  return (
    <div className='container py-6 max-w-6xl mx-auto'>
      {approvalBanner && (
        <div className='us-card rounded-[20px] border-l-4 border-sapphire'>
          <div className='flex items-start gap-3'>
            <i className='fa-solid fa-clock text-2xl text-sapphire mt-1' aria-hidden='true' />
            <div className='flex-1'>
              <h3 className='font-semibold mb-1'>
                {approvalBanner.profileStatusMessage?.title ?? "Let's get your profile approved"}
              </h3>
              {approvalBanner.profileStatusMessage?.body && (
                <p className='mb-2 text-sm'>{approvalBanner.profileStatusMessage.body}</p>
              )}
              <a
                id='approval-checklist-link'
                href={approvalBanner.profileStatusMessage?.url ?? '/app/sitter/success-guide'}
                className='btn btn-primary inline-block'
              >
                {t('pages.sitterProfile.index.approvalChecklistLink')}
              </a>
            </div>
          </div>
        </div>
      )}
      <NotAcceptingFamiliesBanner sitter={sitter} />
      <SitterProfileChrome
        sitter={sitter as unknown as Parameters<typeof SitterProfileChrome>[0]['sitter']}
        activeTab='summary'
        viewerUserId={user?.id}
      />

      <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
        <div className='md:col-span-8 lg:col-span-9'>
          <SitterSnippet
            sitter={sitter}
            isSelf={isSelf}
            isMember={isMember}
            isSitter={isSitter}
            rateText={rateText}
            rateLabel={rateLabel}
            distanceText=''
            onPlayVideo={(): void => setVideoOpen(true)}
            onFavoriteToggle={toggleFavorite}
          />
          {sitter.userStats?.lastAccessLabel && (
            <div className='mb-3 text-sm text-neutral-600'>
              <FontAwesomeIcon icon={faBolt} className='text-sapphire mr-1' />
              <span>Active {sitter.userStats.lastAccessLabel.toLowerCase()}</span>
            </div>
          )}
          <AvailabilityStatusWrapper
            sitter={sitter}
            canEdit={canEdit}
            stats={stats}
            onOpenAvailabilityModal={(): void => setAvailabilityOpen(true)}
          />
          <SitterRelationship
            sitter={sitter}
            userId={user?.id}
            onOpenNoteModal={(): void => setNoteOpen(true)}
          />
        </div>

        <div id='profile-side-panel' className='md:col-span-4 lg:col-span-3'>
          <SitterProfileSidePanel
            sitter={sitter}
            user={user}
            isParent={isParent}
            isSelf={isSelf}
            canEdit={canEdit}
            page='summary'
            onBook={onBook}
            onMessage={onMessage}
            onInterview={onInterview}
            onToggleHidden={(): void => setHideConfirmOpen(true)}
            onOpenFlagModal={(): void => setFlagOpen(true)}
            onReviewClick={(): void => navigate(`/sitter/${sitter.id}/reviews`)}
            recommendLink={recommendLink}
          />
        </div>

        <div id='profile-details' className='md:col-span-8 lg:col-span-9 space-y-8'>
          {/* sitter-about-wrapper:
              col-md-8 = About+edit + Other Services+edit (stacked); col-md-4 = Badges. */}
          <CollapsibleSection title={`About ${sitter.firstName ?? ''}`}>
            <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
              <div className='md:col-span-8 space-y-4 [&>div]:bg-white [&>div]:border [&>div]:border-neutral-200 [&>div]:rounded [&>div]:p-4'>
                <div>
                  {canEdit && (
                    <div className='text-right mb-2'>
                      <Button
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => setEditAboutOpen(true)}
                        className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                        aria-label={t('common.edit')}
                        title={t('common.edit')}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </Button>
                    </div>
                  )}
                  <SitterAbout
                    sitter={sitter}
                    canEdit={canEdit}
                    onOpenEditAbout={(): void => setEditAboutOpen(true)}
                    onOpenEditLanguages={(): void => setEditLanguagesOpen(true)}
                  />
                </div>
                <div>
                  {canEdit && (
                    <div className='text-right mb-2'>
                      <Button
                        buttonType={ButtonType.tertiary}
                        onClick={(): void => setEditOtherServicesOpen(true)}
                        className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                        aria-label={t('common.edit')}
                        title={t('common.edit')}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </Button>
                    </div>
                  )}
                  <SitterOtherServices sitter={sitter} canEdit={canEdit} />
                </div>
              </div>
              <div className='md:col-span-4 bg-white border border-neutral-200 rounded p-4'>
                <SitterBadges sitter={sitter} />
              </div>
            </div>
          </CollapsibleSection>

          {/* sitter-experience-wrapper: 3 cols x 2 rows */}
          <CollapsibleSection title={t('pages.sitterProfile.index.sectionExperience')}>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 [&>div]:bg-white [&>div]:border [&>div]:border-neutral-200 [&>div]:rounded [&>div]:p-4'>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      id='edit-age-experience-button'
                      onClick={(): void => setEditAgeOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label={t('common.edit')}
                      title={t('common.edit')}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterAgeExperience sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditRatesOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label={t('common.edit')}
                      title={t('common.edit')}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterRates sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditTeachingSkillsOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label={t('common.edit')}
                      title={t('common.edit')}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterTeachingSkills sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditSpecialExpOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label={t('common.edit')}
                      title={t('common.edit')}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterSpecialExperience sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditSpecialServicesOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label={t('common.edit')}
                      title={t('common.edit')}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterSpecialServices sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      onClick={(): void => setEditSpecialSituationsOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label={t('common.edit')}
                      title={t('common.edit')}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterSpecialSituations sitter={sitter} />
              </div>
            </div>
          </CollapsibleSection>

          {/* sitter-background-wrapper: Education + Certifications in 2-col */}
          <CollapsibleSection title={t('pages.sitterProfile.index.sectionBackground')}>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 [&>div]:bg-white [&>div]:border [&>div]:border-neutral-200 [&>div]:rounded [&>div]:p-4'>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      id='edit-education-button'
                      onClick={(): void => setEditEducationOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label={t('common.edit')}
                      title={t('common.edit')}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterEducation sitter={sitter} />
              </div>
              <div>
                {canEdit && (
                  <div className='text-right mb-1'>
                    <Button
                      buttonType={ButtonType.tertiary}
                      id='edit-certifications-button'
                      onClick={(): void => setEditCertificationsOpen(true)}
                      className='cursor-pointer text-sapphire text-sm bg-transparent border-0 p-0'
                      aria-label={t('common.edit')}
                      title={t('common.edit')}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                  </div>
                )}
                <SitterCertifications sitter={sitter} />
              </div>
            </div>
          </CollapsibleSection>

          {/* sitter-location-wrapper */}
          <CollapsibleSection title={t('pages.sitterProfile.index.sectionLocation')}>
            <SitterLocation
              sitter={sitter}
              isSelf={isSelf}
              onEditLocation={(): void => setEditLocationOpen(true)}
            />
          </CollapsibleSection>
        </div>
      </div>

      <ProfileVideoModal
        isOpen={videoOpen}
        onClose={(): void => setVideoOpen(false)}
        vimeoId={sitter.vimeoId}
      />
      {sitter.id && user?.id && (
        <SitterNoteModal
          isOpen={noteOpen}
          onClose={(): void => setNoteOpen(false)}
          sitterId={sitter.id}
          parentId={user.id}
          initialNote={
            sitter.relationship?.parentNote as
              | { id?: number; comment?: string }
              | undefined
          }
          onSaved={(): void => {
            refreshSitter();
          }}
        />
      )}
      {sitter.id && user?.id && (
        <FlagProfileModal
          isOpen={flagOpen}
          onClose={(): void => setFlagOpen(false)}
          flaggedId={sitter.id}
          flaggingId={user.id}
        />
      )}
      <ConfirmModal
        isOpen={hideConfirmOpen}
        onClose={(): void => setHideConfirmOpen(false)}
        title={
          sitter.relationship?.blocked
            ? 'Show this caregiver?'
            : 'Hide this caregiver?'
        }
        body={
          sitter.relationship?.blocked
            ? 'You will see this caregiver in search results again.'
            : 'This caregiver will no longer appear in your search results.'
        }
        confirmLabel={sitter.relationship?.blocked ? 'Show' : 'Hide'}
        onConfirm={toggleHidden}
      />
      <AvailabilityModal
        isOpen={availabilityOpen}
        onClose={(): void => setAvailabilityOpen(false)}
        sitter={sitter}
        canEdit={canEdit}
        onSaved={refreshSitter}
        lastCalendarUpdateLabel={
          (sitter as unknown as { stats?: { lastCalendarUpdate?: { label?: string } } })
            .stats?.lastCalendarUpdate?.label
        }
      />
      <BasicsAndBioEditModal
        isOpen={editAboutOpen}
        onClose={(): void => setEditAboutOpen(false)}
        firstName={sitter.firstName}
        initialDob={sitter.dob}
        initialEnglish={sitter.english}
        initialAbout={sitter.about}
        onSave={saveAbout}
      />

      <MultiSelectEditModal
        isOpen={editTeachingSkillsOpen}
        onClose={(): void => setEditTeachingSkillsOpen(false)}
        title={t('pages.sitterProfile.index.editTeachingSkillsTitle')}
        options={teachingSkillsOptions.map((o) => ({ id: o.id, name: o.name }))}
        initialSelected={(sitter.teachingSkills ?? []).map((s) => s.id)}
        onSave={saveTeachingSkills}
        columns={3}
      />

      <MultiSelectEditModal
        isOpen={editSpecialExpOpen}
        onClose={(): void => setEditSpecialExpOpen(false)}
        title={t('pages.sitterProfile.index.editSpecialExperienceTitle')}
        options={specialExpOptions}
        initialSelected={(sitter.specialExperiences ?? []).map((s) => s.id)}
        onSave={(ids): Promise<void> => savePatchField('specialExperiences', specialExpOptions, ids)}
        columns={3}
      />

      <MultiSelectEditModal
        isOpen={editSpecialServicesOpen}
        onClose={(): void => setEditSpecialServicesOpen(false)}
        title={t('pages.sitterProfile.index.editSpecialServicesTitle')}
        options={specialServicesOptions}
        initialSelected={(sitter.specialServices ?? []).map((s) => s.id)}
        onSave={(ids): Promise<void> => savePatchField('specialServices', specialServicesOptions, ids)}
        columns={2}
        maxWidth='max-w-2xl'
      />

      <MultiSelectEditModal
        isOpen={editSpecialSituationsOpen}
        onClose={(): void => setEditSpecialSituationsOpen(false)}
        title={t('pages.sitterProfile.index.selectSituationsTitle')}
        options={specialSituationsOptions}
        initialSelected={(sitter.specialSituations ?? []).map((s) => s.id)}
        onSave={(ids): Promise<void> => saveSpecialSituations(ids)}
        columns={3}
      />

      <MultiSelectEditModal
        isOpen={editLanguagesOpen}
        onClose={(): void => setEditLanguagesOpen(false)}
        title={t('pages.sitterProfile.index.editLanguagesTitle')}
        options={languagesOptions}
        initialSelected={(sitter.languages ?? [])
          .map((language) =>
            languagesOptions.find(
              (option) =>
                option.id === language.id || option.name.toLowerCase() === (language.name ?? '').toLowerCase(),
            )?.id,
          )
          .filter((id): id is number => id != null)}
        onSave={(ids, otherName): Promise<void> =>
          savePatchField('languages', languagesOptions, ids, otherName)
        }
        allowOther
        otherLabel='Other:'
        otherPlaceholder='Other Language'
        initialOther={initialOtherLanguage}
        columns={2}
        maxWidth='max-w-2xl'
      />

      <OtherServicesEditModal
        isOpen={editOtherServicesOpen}
        onClose={(): void => setEditOtherServicesOpen(false)}
        initialWillSitDog={sitter.willSitDog}
        initialWillSitCat={sitter.willSitCat}
        initialWillSitOtherPet={sitter.willSitOtherPet}
        initialWillSitSenior={sitter.willSitSenior}
        initialWillSitHome={sitter.willSitHome}
        initialPetSittingExperience={sitter.petSittingExperience}
        initialSeniorSittingExperience={sitter.seniorSittingExperience}
        initialHomeSittingExperience={sitter.homeSittingExperience}
        initialSpecialServiceIds={(sitter.specialServices ?? []).map((s) => s.id)}
        specialServices={specialServicesOptions.map((s) => ({ id: s.id, name: s.name }))}
        onSave={saveOtherServices}
      />

      <RatesEditModal
        isOpen={editRatesOpen}
        onClose={(): void => setEditRatesOpen(false)}
        initialRates={sitter.rates ?? []}
        initialMaxKids={sitter.maxKids}
        initialMinimumHours={sitter.minimumHours}
        initialAcceptsFixedRates={sitter.acceptsFixedRates}
        initialWillSitSingleFamily={sitter.willSitSingleFamily}
        suggestedRate={suggestedRate}
        onSave={saveRates}
      />

      <LocationEditModal
        isOpen={editLocationOpen}
        onClose={(): void => setEditLocationOpen(false)}
        initialAddress={sitter.address as Partial<{ street: string; street2: string; city: string; state: string; zip: string; neighborhood: string; }>}
        initialWorksWithin={sitter.worksWithin}
        onSave={saveLocation}
      />

      <AgeExperienceEditModal
        isOpen={editAgeOpen}
        onClose={(): void => setEditAgeOpen(false)}
        ageExperiences={ageExperiencesOptions}
        initialExperiences={sitter.experiences}
        onSave={saveAgeExperiences}
      />

      <EducationEditModal
        isOpen={editEducationOpen}
        onClose={(): void => setEditEducationOpen(false)}
        initialEducation={education}
        onSave={saveEducation}
        onDelete={deleteEducation}
      />

      <CertificationsEditModal
        isOpen={editCertificationsOpen}
        onClose={(): void => setEditCertificationsOpen(false)}
        certifications={certificationsOptions}
        initialSelected={(sitter.certifications ?? []).map((c) => ({
          id: c.id,
          name: c.name ?? '',
        }))}
        onSave={saveCertifications}
      />
    </div>
  );
});
